import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { get, ref } from 'firebase/database';

import { db } from '../utils/firebaseApp.js';
import { AuthContext } from '../utils/auth.js';
import { isAdminEmail } from '../utils/adminUsers.js';

export const IMPERSONATION_STORAGE_KEY = 'extracta-view-as';
export const IMPERSONATION_QUERY_PARAM = 'adminUserId';

const ImpersonationContext = createContext({
  canImpersonate: false,
  isImpersonating: false,
  impersonated: null,
  userId: null,
  start: () => {},
  stop: () => {},
  pickerOpen: false,
  openPicker: () => {},
  closePicker: () => {},
});

function readStored() {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeStored(value) {
  try {
    if (value) sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch (error) {
    // storage unavailable
  }
}

/** Looks up a user's email / name from the realtime database. */
export async function lookupUserMeta(userId) {
  try {
    const snapshot = await get(ref(db, `userDetails/${userId}`));
    if (!snapshot.exists()) return { email: '', name: '' };
    const data = snapshot.val() || {};
    return {
      email: data.email || data.account?.emailAddress || '',
      name: data.name || data.account?.fullName || '',
    };
  } catch (error) {
    return { email: '', name: '' };
  }
}

/**
 * Global "View as user" state for admins. Screens call `useEffectiveUserId()`
 * instead of `currentUser.uid` so the whole app follows the selection.
 * The selection persists for the browser session and is mirrored in the
 * `?adminUserId=` query param so links can be shared between admins.
 */
export function ImpersonationProvider({ children }) {
  const { currentUser } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const canImpersonate = isAdminEmail(currentUser?.email);

  const [impersonated, setImpersonated] = useState(() => readStored());
  const [pickerOpen, setPickerOpen] = useState(false);

  // Adopt ?adminUserId= from the URL (deep links from the admin console).
  useEffect(() => {
    if (!canImpersonate) return;
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get(IMPERSONATION_QUERY_PARAM);
    if (fromUrl && fromUrl !== impersonated?.userId) {
      const next = { userId: fromUrl, email: '', name: '', startedAt: Date.now() };
      setImpersonated(next);
      writeStored(next);
      lookupUserMeta(fromUrl).then((meta) => {
        setImpersonated((current) => (current?.userId === fromUrl ? { ...current, ...meta } : current));
      });
    }
  }, [canImpersonate, location.search, impersonated?.userId]);

  // Non-admins never impersonate, even with stale storage.
  useEffect(() => {
    if (!canImpersonate && impersonated) {
      setImpersonated(null);
      writeStored(null);
    }
  }, [canImpersonate, impersonated]);

  // Keep stored meta fresh.
  useEffect(() => {
    if (canImpersonate && impersonated) writeStored(impersonated);
  }, [canImpersonate, impersonated]);

  const start = useCallback(
    async (userId, meta = {}, { navigateTo } = {}) => {
      if (!canImpersonate || !userId) return;
      const id = String(userId).trim();
      const next = { userId: id, email: meta.email || '', name: meta.name || '', startedAt: Date.now() };
      setImpersonated(next);
      writeStored(next);
      setPickerOpen(false);
      if (!meta.email) {
        lookupUserMeta(id).then((fetched) => {
          setImpersonated((current) => (current?.userId === id ? { ...current, ...fetched } : current));
        });
      }
      const params = new URLSearchParams(navigateTo ? '' : location.search);
      params.set(IMPERSONATION_QUERY_PARAM, id);
      history.push(`${navigateTo || location.pathname}?${params.toString()}`);
    },
    [canImpersonate, history, location.pathname, location.search],
  );

  const stop = useCallback(() => {
    setImpersonated(null);
    writeStored(null);
    const params = new URLSearchParams(location.search);
    if (params.has(IMPERSONATION_QUERY_PARAM)) {
      params.delete(IMPERSONATION_QUERY_PARAM);
      const qs = params.toString();
      history.replace(qs ? `${location.pathname}?${qs}` : location.pathname);
    }
  }, [history, location.pathname, location.search]);

  const value = useMemo(() => {
    const active = canImpersonate && impersonated?.userId && impersonated.userId !== currentUser?.uid;
    return {
      canImpersonate,
      isImpersonating: Boolean(active),
      impersonated: active ? impersonated : null,
      userId: active ? impersonated.userId : currentUser?.uid || null,
      start,
      stop,
      pickerOpen,
      openPicker: () => setPickerOpen(true),
      closePicker: () => setPickerOpen(false),
    };
  }, [canImpersonate, impersonated, currentUser?.uid, start, stop, pickerOpen]);

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}

/**
 * The user id every data screen should read from: the impersonated user for
 * admins in "view as" mode, otherwise the signed-in user.
 */
export function useEffectiveUserId() {
  const { userId } = useContext(ImpersonationContext);
  const { currentUser } = useContext(AuthContext);
  return userId || currentUser?.uid || null;
}

/** Appends ?adminUserId= to an in-app path while impersonating. */
export function useImpersonatedPath() {
  const { isImpersonating, impersonated } = useContext(ImpersonationContext);
  return useCallback(
    (path) => {
      if (!isImpersonating || !impersonated?.userId) return path;
      const [pathname, search = ''] = String(path).split('?');
      const params = new URLSearchParams(search);
      params.set(IMPERSONATION_QUERY_PARAM, impersonated.userId);
      return `${pathname}?${params.toString()}`;
    },
    [isImpersonating, impersonated?.userId],
  );
}
